import { GRAPH_VISUAL_THEME, type GraphVisualTheme } from './panelVisualTheme.ts';
import type cytoscape from 'cytoscape';
import type { ELK } from 'elkjs/lib/elk-api';

import type { GraphRenderer, GraphRenderRequest } from './panelGraphRenderer.ts';
import type { Selection } from './panelTypes.ts';
import { graphRelationFocus, graphFocusStyles } from './panelGraphFocus.ts';
import {
    applyGraphLayout,
    layoutGraphScene,
    syncExpandedGroupLabelGeometry,
    type GraphLayoutResult,
    type GraphPosition,
} from './panelGraphLayout.ts';
import {
    selectionKey,
    type GraphScene,
    type GraphSceneEdge,
    type GraphSceneElementId,
    type GraphSceneNode,
} from './panelGraphScene.ts';
import {
    createGraphStyles,
    fitGraphLabel,
    fitCollapsedGroupLabel,
    graphLabelFontSize,
    expandedGroupLabelMaxWidth,
    GRAPH_GEOMETRY,
    graphEdgeDisplayLabel,
    graphLayoutGeometryKey,
    isOverviewGraphScale,
    nodeDimensions,
} from './panelGraphVisuals.ts';

type Viewport = { readonly pan: GraphPosition; readonly zoom: number };

type GraphRendererRuntime = {
    readonly createCore: (options: cytoscape.CytoscapeOptions) => cytoscape.Core;
    readonly elk: ELK;
};

export type GraphResizeSubscription = { disconnect(): void };

export type GraphRendererEnvironment = {
    readonly createElement: (tagName: string) => HTMLElement;
    readonly loadRuntime: () => Promise<GraphRendererRuntime>;
    readonly layoutScene: (elk: ELK, scene: GraphScene, theme?: GraphVisualTheme) => Promise<GraphLayoutResult>;
    readonly observeResize?: (element: HTMLElement, onResize: () => void) => GraphResizeSubscription;
};

export class CytoscapeGraphRenderer implements GraphRenderer {
    private readonly canvasHost: HTMLElement;
    private readonly tooltip: HTMLElement;
    private readonly status: HTMLElement;
    private readonly statusMessage: HTMLElement;
    private readonly retryButton: HTMLButtonElement;
    private readonly resizeSubscription: GraphResizeSubscription | undefined;
    private theme: GraphVisualTheme = GRAPH_VISUAL_THEME;
    private geometryRevision = 0;
    private wasVisible = false;
    private core: cytoscape.Core | undefined;
    private elk: ELK | undefined;
    private loading: Promise<void> | undefined;
    private processing: Promise<void> | undefined;
    private latestRequest: GraphRenderRequest | undefined;
    private appliedScene: GraphScene | undefined;
    private requestVersion = 0;
    private processedVersion = 0;
    private destroyed = false;
    private fitTargetContentKey: string | undefined;
    private forceRelayout = false;
    private anchorElementId: GraphSceneElementId | undefined;
    private anchorTargetContentKey: string | undefined;
    private failedSceneContentKey: string | undefined;
    private overview = false;
    private dragPanning = false;
    private pointerInside = false;
    private pointerOverElement = false;
    private readonly cancelPointerInteraction = () => {
        this.dragPanning = false;
        this.cancelGroupTap();
        this.clearHover();
    };
    private pendingReveal: { readonly request: NonNullable<GraphRenderRequest['reveal']>; readonly contentKey: string; readonly captureRevision: number | undefined } | undefined;
    private pendingGroupTap: { readonly id: string; readonly timer: ReturnType<typeof setTimeout> } | undefined;

    constructor(
        private readonly host: HTMLElement,
        private readonly environment: GraphRendererEnvironment = browserGraphRendererEnvironment,
    ) {
        this.canvasHost = environment.createElement('div');
        this.canvasHost.className = 'zenfg-inspector-graph-canvas';
        this.updateCursor();
        this.tooltip = environment.createElement('div');
        this.tooltip.className = 'zenfg-inspector-graph-tooltip';
        this.tooltip.hidden = true;
        // Cytoscape's node mouseout is not guaranteed when leaving its canvas directly.
        this.canvasHost.addEventListener('pointerenter', () => { this.pointerInside = true; });
        this.canvasHost.addEventListener('pointerleave', () => { this.pointerInside = false; this.clearHover(); });
        this.canvasHost.addEventListener('pointercancel', this.cancelPointerInteraction);
        this.canvasHost.ownerDocument?.defaultView?.addEventListener('blur', this.cancelPointerInteraction);
        this.status = environment.createElement('div');
        this.status.className = 'zenfg-inspector-graph-status';
        this.status.hidden = true;
        this.status.setAttribute('role', 'status');
        this.status.setAttribute('aria-live', 'polite');
        this.statusMessage = environment.createElement('span');
        this.retryButton = environment.createElement('button') as HTMLButtonElement;
        this.retryButton.type = 'button';
        this.retryButton.textContent = 'Retry';
        this.retryButton.hidden = true;
        this.retryButton.addEventListener('click', () => this.relayout());
        this.status.append(this.statusMessage, this.retryButton);
        this.host.replaceChildren(this.canvasHost, this.status, this.tooltip);
        this.resizeSubscription = environment.observeResize?.(host, () => {
            const visible = host.clientWidth > 0 && host.clientHeight > 0;
            if (visible && !this.wasVisible) this.latestRequest?.onVisible?.();
            this.wasVisible = visible;
            this.core?.resize();
        });
    }

    render(request: GraphRenderRequest): void {
        if (this.destroyed) return;
        if (request.theme) this.setTheme(request.theme);
        if (this.latestRequest?.scene !== request.scene
            || (this.latestRequest?.selected && selectionKey(this.latestRequest.selected)) !== (request.selected && selectionKey(request.selected))) {
            this.cancelGroupTap();
        }
        if ((!request.hovered && !this.tooltip.hidden) || this.latestRequest?.scene.contentKey !== request.scene.contentKey) {
            this.hideTooltip();
            this.core?.elements().removeClass('semantic-hover');
        }
        this.latestRequest = request;
        if (request.reveal) {
            this.pendingReveal = { request: request.reveal, contentKey: request.scene.contentKey, captureRevision: request.captureRevision };
        } else if (this.pendingReveal && (this.pendingReveal.captureRevision !== request.captureRevision
            || this.pendingReveal.contentKey !== request.scene.contentKey
            || !request.selected || selectionKey(request.selected) !== selectionKey(this.pendingReveal.request.selection))) {
            this.pendingReveal = undefined;
        }
        if (request.fit) {
            this.fitTargetContentKey = request.scene.contentKey;
        } else if (this.fitTargetContentKey !== request.scene.contentKey) {
            this.fitTargetContentKey = undefined;
        }
        if (request.anchorElementId !== undefined) {
            this.anchorElementId = request.anchorElementId;
            this.anchorTargetContentKey = request.scene.contentKey;
        } else if (this.anchorTargetContentKey !== request.scene.contentKey) {
            this.anchorElementId = undefined;
            this.anchorTargetContentKey = undefined;
        }
        if (this.failedSceneContentKey && this.failedSceneContentKey !== request.scene.contentKey) {
            this.failedSceneContentKey = undefined;
            this.hideStatus();
        }

        const version = ++this.requestVersion;
        if (this.failedSceneContentKey === request.scene.contentKey) {
            this.syncInteraction(request);
            this.processedVersion = version;
            return;
        }
        if (!this.processing && this.core && this.appliedScene?.contentKey === request.scene.contentKey && !this.forceRelayout) {
            this.appliedScene = request.scene;
            this.syncInteraction(request);
            this.consumeFitIfCurrentTarget();
            this.consumeAnchorIfCurrentTarget();
            this.consumeReveal();
            this.processedVersion = version;
            return;
        }
        this.scheduleProcessing();
    }

    setTheme(theme: GraphVisualTheme): void {
        if (this.destroyed || JSON.stringify(theme) === JSON.stringify(this.theme)) return;
        const typographyChanged = theme.fontFamily !== this.theme.fontFamily || theme.fontSize !== this.theme.fontSize;
        this.theme = theme;
        this.core?.style().fromJson([...createGraphStyles(theme), ...graphFocusStyles()]).update();
        if (!typographyChanged) return;
        this.geometryRevision++;
        if (this.core && this.latestRequest && this.appliedScene) {
            const selected = this.core.nodes('.semantic-selected').first();
            const center = this.core.extent();
            const target = selected.nonempty() ? selected : this.core.nodes().filter(node => !node.isParent()).sort((a, b) => {
                const distance = (node: cytoscape.NodeSingular) => Math.hypot(node.position('x') - center.x1 - center.w / 2, node.position('y') - center.y1 - center.h / 2);
                return distance(a as cytoscape.NodeSingular) - distance(b as cytoscape.NodeSingular);
            }).first();
            if (target.nonempty()) {
                this.anchorElementId = target.id();
                this.anchorTargetContentKey = this.latestRequest.scene.contentKey;
            }
            this.forceRelayout = true;
            ++this.requestVersion;
            this.scheduleProcessing();
        }
        this.updateSemanticZoom(true);
    }

    resize(): void {
        this.core?.resize();
    }

    cancelReveal(): void {
        this.pendingReveal = undefined;
        this.cancelGroupTap();
        this.clearHover();
    }

    fit(): void {
        this.cancelViewportRequests();
        const targetContentKey = this.latestRequest?.scene.contentKey;
        if (targetContentKey) this.fitTargetContentKey = targetContentKey;
        if (!this.core || !this.appliedScene) return;
        this.core.resize();
        this.core.fit(undefined, GRAPH_GEOMETRY.fitPadding);
        this.updateSemanticZoom();
        if (!this.targetNeedsApply()) this.fitTargetContentKey = undefined;
    }

    zoomBy(factor: number): void {
        if (!this.core || !Number.isFinite(factor) || factor <= 0) return;
        this.zoomTo(this.core.zoom() * factor);
    }

    resetZoom(): void { this.zoomTo(1); }

    fitSelection(selection: Selection): void {
        const request = this.interactiveRequest();
        const core = this.core;
        if (!request || !core || this.targetNeedsApply()) return;
        const targets = core.collection();
        for (const id of request.scene.interaction.primaryElementIdsBySelection.get(selectionKey(selection)) ?? []) {
            targets.merge(core.getElementById(id));
        }
        if (!targets.length) return;
        this.cancelViewportRequests();
        core.resize();
        core.fit(targets, 64);
        if (core.zoom() > 1.2) core.zoom(1.2);
        core.center(targets);
        this.updateSemanticZoom();
    }

    private zoomTo(level: number): void {
        const core = this.core;
        if (!core || this.targetNeedsApply()) return;
        this.cancelViewportRequests();
        core.resize();
        core.zoom({ level: Math.max(core.minZoom(), Math.min(core.maxZoom(), level)),
            renderedPosition: { x: core.width() / 2, y: core.height() / 2 } });
        this.updateSemanticZoom();
    }

    private cancelViewportRequests(): void {
        this.pendingReveal = undefined;
        this.fitTargetContentKey = undefined;
        this.anchorElementId = undefined;
        this.anchorTargetContentKey = undefined;
    }

    relayout(): void {
        if (!this.latestRequest || this.destroyed) return;
        this.cancelGroupTap();
        this.failedSceneContentKey = undefined;
        this.forceRelayout = true;
        this.fitTargetContentKey = this.latestRequest.scene.contentKey;
        this.anchorElementId = undefined;
        this.anchorTargetContentKey = undefined;
        this.hideTooltip();
        ++this.requestVersion;
        this.scheduleProcessing();
    }

    destroy(): void {
        if (this.destroyed) return;
        this.destroyed = true;
        this.cancelGroupTap();
        this.requestVersion++;
        this.resizeSubscription?.disconnect();
        this.canvasHost.ownerDocument?.defaultView?.removeEventListener('blur', this.cancelPointerInteraction);
        this.hideTooltip();
        this.core?.destroy();
        this.core = undefined;
        this.elk = undefined;
        this.loading = undefined;
        this.latestRequest = undefined;
        this.appliedScene = undefined;
        this.fitTargetContentKey = undefined;
        this.anchorElementId = undefined;
        this.anchorTargetContentKey = undefined;
        this.host.replaceChildren();
    }

    private scheduleProcessing(): void {
        if (this.processing || this.destroyed) return;
        this.processing = this.processRequests().finally(() => {
            this.processing = undefined;
            if (!this.destroyed
                && !this.failedSceneContentKey
                && this.processedVersion !== this.requestVersion) {
                this.scheduleProcessing();
            }
        });
    }

    private async processRequests(): Promise<void> {
        while (!this.destroyed && this.processedVersion !== this.requestVersion) {
            let version = this.requestVersion;
            let request = this.latestRequest;
            if (!request) return;

            if (request.scene.nodes.length === 0) {
                this.hideTooltip();
                if (this.core) replaceElements(this.core, request.scene, this.theme);
                this.appliedScene = request.scene;
                this.forceRelayout = false;
                if (this.fitTargetContentKey === request.scene.contentKey) this.fitTargetContentKey = undefined;
                this.consumeAnchorIfCurrentTarget();
                this.syncInteraction(request);
                this.showStatus('empty', 'No graph elements in this view.');
                this.processedVersion = version;
                continue;
            }

            try {
                if (!this.core) this.showStatus('loading', 'Loading graph runtime…');
                await this.ensureRuntime();
                const runtimeRequest = this.resolveCurrentRequest(version, request.scene.contentKey);
                if (!runtimeRequest) continue;
                ({ version, request } = runtimeRequest);
                const core = this.core!;
                if (this.appliedScene?.contentKey === request.scene.contentKey && !this.forceRelayout) {
                    this.appliedScene = request.scene;
                    this.syncInteraction(request);
                    this.consumeFitIfCurrentTarget();
                    this.consumeAnchorIfCurrentTarget();
                    this.consumeReveal();
                    this.hideStatus();
                    this.processedVersion = version;
                    continue;
                }

                this.hideTooltip();
                const topologyChanged = this.appliedScene?.topologyKey !== request.scene.topologyKey;
                const geometryChanged = !this.appliedScene
                    || graphLayoutGeometryKey(this.appliedScene) !== graphLayoutGeometryKey(request.scene);
                let layoutResult: GraphLayoutResult | undefined;

                if (geometryChanged || this.forceRelayout) {
                    this.showStatus('layout', 'Laying out graph…');
                    const geometryRevision = this.geometryRevision;
                    layoutResult = await this.environment.layoutScene(this.elk!, request.scene, this.theme);
                    if (geometryRevision !== this.geometryRevision) continue;
                    const layoutRequest = this.resolveCurrentRequest(version, request.scene.contentKey);
                    if (!layoutRequest) continue;
                    ({ version, request } = layoutRequest);
                }

                // Read after layout settles so dragging or zooming during layout is retained.
                const previousViewport = viewport(core);
                const anchorElementId = this.anchorTargetContentKey === request.scene.contentKey
                    ? this.anchorElementId
                    : undefined;
                const anchorBefore = anchorElementId === undefined
                    ? undefined
                    : renderedPosition(core, anchorElementId);
                const laidOut = layoutResult !== undefined;
                if (layoutResult) {
                    if (topologyChanged || !this.appliedScene) replaceElements(core, request.scene, this.theme);
                    else updateElementData(core, request.scene, this.theme);
                    applyGraphLayout(core, request.scene, layoutResult);
                } else {
                    updateElementData(core, request.scene, this.theme);
                }

                syncExpandedGroupLabelGeometry(core);
                this.appliedScene = request.scene;
                this.forceRelayout = false;
                this.failedSceneContentKey = undefined;
                this.syncInteraction(request);
                core.resize();
                if (this.fitTargetContentKey === request.scene.contentKey) {
                    core.fit(undefined, GRAPH_GEOMETRY.fitPadding);
                    this.fitTargetContentKey = undefined;
                } else if (laidOut && anchorBefore && anchorElementId) {
                    restoreViewport(core, previousViewport);
                    const anchorAfter = renderedPosition(core, anchorElementId);
                    if (anchorAfter) core.panBy({ x: anchorBefore.x - anchorAfter.x, y: anchorBefore.y - anchorAfter.y });
                } else {
                    restoreViewport(core, previousViewport);
                }
                this.consumeAnchorIfCurrentTarget();
                this.consumeReveal();
                this.updateSemanticZoom(true);
                this.hideStatus();
                this.processedVersion = version;
            } catch (error) {
                const failedRequest = this.resolveCurrentRequest(version, request.scene.contentKey);
                if (!failedRequest) continue;
                ({ version, request } = failedRequest);
                this.forceRelayout = false;
                this.failedSceneContentKey = request.scene.contentKey;
                this.processedVersion = version;
                this.showStatus(
                    'error',
                    `Failed to render graph: ${error instanceof Error ? error.message : String(error)}`,
                    true,
                );
                return;
            }
        }
    }

    private async ensureRuntime(): Promise<void> {
        if (this.core || this.destroyed) return;
        this.loading ??= this.loadRuntime().catch((error) => {
            this.loading = undefined;
            throw error;
        });
        await this.loading;
    }

    private async loadRuntime(): Promise<void> {
        const runtime = await this.environment.loadRuntime();
        if (this.destroyed) return;
        this.canvasHost.textContent = '';
        this.elk = runtime.elk;
        this.core = runtime.createCore({
            container: this.canvasHost,
            elements: [],
            style: [...createGraphStyles(this.theme), ...graphFocusStyles()],
            layout: { name: 'preset' },
            minZoom: 0.03,
            maxZoom: 4,
            wheelSensitivity: 1.3,
            boxSelectionEnabled: false,
            autoungrabify: true,
            autounselectify: true,
        });
        this.bindEvents(this.core);
    }

    private bindEvents(core: cytoscape.Core): void {
        const selectElement = (event: cytoscape.EventObject) => {
            const request = this.interactiveRequest();
            if (!request) return;
            const id = event.target.id() as GraphSceneElementId;
            const selection = request.scene.interaction.selectionByElementId.get(id);
            const doubleTap = this.pendingGroupTap?.id === id;
            this.cancelGroupTap();
            if (selection?.kind !== 'group') {
                if (selection) request.onSelect(selection);
            } else if (doubleTap) {
                request.onToggleGroup(selection.pathKey);
            } else {
                // Defer group selection so expansion does not replace the inspected resource.
                this.pendingGroupTap = { id, timer: setTimeout(() => {
                    this.pendingGroupTap = undefined;
                    this.interactiveRequest()?.onSelect(selection);
                }, 350) };
            }
        };
        core.on('tap', 'node', selectElement);
        core.on('tap', 'edge', selectElement);
        core.on('tap', (event) => {
            if (event.target === core) this.cancelGroupTap();
        });
        const hoverElement = (event: cytoscape.EventObject) => {
            const request = this.interactiveRequest();
            if (!request || this.dragPanning) return;
            const id = event.target.id() as GraphSceneElementId;
            const selection = request.scene.interaction.selectionByElementId.get(id);
            this.pointerInside = true;
            this.pointerOverElement = !!selection;
            this.updateCursor();
            if (selection) request.onHover(selection);
            const title = event.target.data('tooltip') as string | undefined;
            if (title) {
                this.tooltip.textContent = title;
                this.tooltip.hidden = false;
                this.positionTooltip(event.renderedPosition);
            }
        };
        core.on('mouseover', 'node', hoverElement);
        core.on('mouseover', 'edge', hoverElement);
        const moveTooltip = (event: cytoscape.EventObject) => this.positionTooltip(event.renderedPosition);
        core.on('mousemove', 'node', moveTooltip);
        core.on('mousemove', 'edge', moveTooltip);
        const clearHover = () => this.clearHover();
        core.on('mouseout', 'node', clearHover);
        core.on('mouseout', 'edge', clearHover);
        core.on('dragpan', () => {
            if (this.dragPanning) return;
            this.dragPanning = true;
            this.cancelGroupTap();
            this.clearHover();
        });
        core.on('tapend', (event) => {
            this.dragPanning = false;
            this.pointerOverElement = this.pointerInside && event.target !== core
                && !!this.interactiveRequest()?.scene.interaction.selectionByElementId.get(event.target.id());
            this.updateCursor();
        });
        core.on('zoom', () => this.updateSemanticZoom());
    }

    private interactiveRequest(): GraphRenderRequest | undefined {
        const request = this.latestRequest;
        return request && this.appliedScene?.contentKey === request.scene.contentKey ? request : undefined;
    }

    private syncInteraction(request: GraphRenderRequest): void {
        const core = this.core;
        if (!core || this.appliedScene?.topologyKey !== request.scene.topologyKey) return;
        core.batch(() => {
            core.elements().removeClass('semantic-selected semantic-hover semantic-muted');
            const focus = request.focusRelations ? graphRelationFocus(request.scene, request.selected) : undefined;
            if (focus) {
                for (const element of core.elements()) {
                    if (!focus.has(element.id())) element.addClass('semantic-muted');
                }
            }
            if (request.hovered) {
                for (const id of request.scene.interaction.hoverElementIdsBySelection.get(selectionKey(request.hovered)) ?? []) {
                    core.getElementById(id).addClass('semantic-hover');
                }
            }
            if (request.selected) {
                for (const id of request.scene.interaction.primaryElementIdsBySelection.get(selectionKey(request.selected)) ?? []) {
                    core.getElementById(id).addClass('semantic-selected');
                }
            }
        });
    }

    private consumeFitIfCurrentTarget(): void {
        const targetContentKey = this.latestRequest?.scene.contentKey;
        if (!targetContentKey
            || this.fitTargetContentKey !== targetContentKey
            || !this.core
            || this.targetNeedsApply()) return;
        this.core.resize();
        this.core.fit(undefined, GRAPH_GEOMETRY.fitPadding);
        this.fitTargetContentKey = undefined;
        this.updateSemanticZoom();
    }

    private consumeAnchorIfCurrentTarget(): void {
        const targetContentKey = this.latestRequest?.scene.contentKey;
        if (!targetContentKey || this.anchorTargetContentKey !== targetContentKey) return;
        this.anchorElementId = undefined;
        this.anchorTargetContentKey = undefined;
    }

    private consumeReveal(): void {
        const pending = this.pendingReveal;
        const request = this.latestRequest;
        if (!pending || !request || !this.core || this.targetNeedsApply()) return;
        this.pendingReveal = undefined;
        if (pending.captureRevision !== request.captureRevision || pending.contentKey !== request.scene.contentKey) return;
        const ids = request.scene.interaction.primaryElementIdsBySelection.get(selectionKey(pending.request.selection)) ?? [];
        const targets = this.core.collection();
        for (const id of ids) targets.merge(this.core.getElementById(id));
        if (!targets.length) return;
        this.core.resize();
        this.core.fit(targets, 64);
        if (this.core.zoom() > 1.2) this.core.zoom(1.2);
        this.core.center(targets);
        this.updateSemanticZoom();
    }

    private targetNeedsApply(): boolean {
        const target = this.latestRequest?.scene;
        if (!target || !this.appliedScene) return target !== undefined;
        return this.forceRelayout || this.appliedScene.contentKey !== target.contentKey;
    }

    private updateSemanticZoom(force = false): void {
        const core = this.core;
        if (!core) return;
        this.latestRequest?.onViewportChange?.(core.zoom());
        const overview = isOverviewGraphScale(core.zoom(), this.theme);
        if (!force && overview === this.overview) return;
        this.overview = overview;
        core.batch(() => {
            for (const node of core.nodes()) {
                node.data('displayLabel', overview ? node.data('overviewLabel') : node.data('detailLabel'));
            }
            for (const edge of core.edges()) {
                edge.data('displayLabel', overview ? '' : edge.data('detailLabel'));
            }
        });
        // fit/center can read bounds before the next canvas frame. Measure the
        // changed labels now so their texture bounds match the new line count.
        // The runtime supports useCache, which its bundled typings omit.
        const boundsOptions: cytoscape.BoundingBoxOptions & { useCache: boolean } = { useCache: false };
        core.nodes().boundingBox(boundsOptions);
    }

    private positionTooltip(position: GraphPosition): void {
        const maxX = Math.max(8, this.host.clientWidth - this.tooltip.offsetWidth - 8);
        const maxY = Math.max(8, this.host.clientHeight - this.tooltip.offsetHeight - 8);
        this.tooltip.style.left = `${Math.max(8, Math.min(maxX, position.x + 12))}px`;
        this.tooltip.style.top = `${Math.max(8, Math.min(maxY, position.y + 12))}px`;
    }

    private hideTooltip(): void {
        this.tooltip.hidden = true;
        this.tooltip.textContent = '';
    }

    private cancelGroupTap(): void {
        if (this.pendingGroupTap) clearTimeout(this.pendingGroupTap.timer);
        this.pendingGroupTap = undefined;
    }

    private clearHover(): void {
        this.pointerOverElement = false;
        this.updateCursor();
        this.hideTooltip();
        this.latestRequest?.onHover(undefined);
    }

    private updateCursor(): void {
        this.canvasHost.style.cursor = this.dragPanning ? 'grabbing' : this.pointerOverElement ? 'pointer' : 'grab';
    }

    private showStatus(kind: 'loading' | 'layout' | 'empty' | 'error', message: string, retry = false): void {
        this.status.dataset.state = kind;
        this.statusMessage.textContent = message;
        this.retryButton.hidden = !retry;
        this.status.hidden = false;
        this.host.setAttribute('aria-busy', kind === 'loading' || kind === 'layout' ? 'true' : 'false');
    }

    private hideStatus(): void {
        this.status.hidden = true;
        this.retryButton.hidden = true;
        this.host.setAttribute('aria-busy', 'false');
    }

    private resolveCurrentRequest(
        version: number,
        contentKey: string,
    ): { readonly version: number; readonly request: GraphRenderRequest } | undefined {
        if (this.destroyed) return undefined;
        const request = this.latestRequest;
        if (!request) return undefined;
        if (version === this.requestVersion || request.scene.contentKey === contentKey) {
            return { version: this.requestVersion, request };
        }
        return undefined;
    }
}

const browserGraphRendererEnvironment: GraphRendererEnvironment = {
    createElement: (tagName) => document.createElement(tagName),
    loadRuntime: async () => {
        const [cytoscapeModule, elkModule] = await Promise.all([
            import('cytoscape'),
            import('elkjs/lib/elk.bundled.js'),
        ]);
        return {
            createCore: cytoscapeModule.default,
            elk: new elkModule.default(),
        };
    },
    layoutScene: layoutGraphScene,
    observeResize: (element, onResize) => {
        if (typeof ResizeObserver === 'undefined') return { disconnect: () => undefined };
        const view = element.ownerDocument.defaultView;
        let animationFrame: number | undefined;
        const observer = new ResizeObserver(() => {
            if (!view || animationFrame !== undefined) return;
            animationFrame = view.requestAnimationFrame(() => {
                animationFrame = undefined;
                onResize();
            });
        });
        observer.observe(element);
        return {
            disconnect: () => {
                observer.disconnect();
                if (animationFrame !== undefined) {
                    view?.cancelAnimationFrame(animationFrame);
                    animationFrame = undefined;
                }
            },
        };
    },
};

function replaceElements(core: cytoscape.Core, scene: GraphScene, theme: GraphVisualTheme = GRAPH_VISUAL_THEME): void {
    const definitions: cytoscape.ElementDefinition[] = [
        ...scene.nodes.map(node => nodeDefinition(node, theme)),
        ...scene.edges.map(edgeDefinition),
    ];
    core.startBatch();
    core.elements().remove();
    core.add(definitions);
    core.endBatch();
}

export function updateElementData(core: cytoscape.Core, scene: GraphScene, theme: GraphVisualTheme = GRAPH_VISUAL_THEME): void {
    core.batch(() => {
        for (const node of scene.nodes) {
            const element = core.getElementById(node.id);
            if (element.nonempty()) element.data(nodeRenderableData(node, theme));
        }
        for (const edge of scene.edges) {
            const element = core.getElementById(edge.id);
            if (element.nonempty()) element.data(edgeRenderableData(edge));
        }
    });
}

function nodeDefinition(node: GraphSceneNode, theme: GraphVisualTheme): cytoscape.ElementDefinition {
    return {
        group: 'nodes',
        pannable: true,
        data: {
            id: node.id,
            parent: node.parentId,
            ...nodeRenderableData(node, theme),
        },
    };
}

function nodeRenderableData(node: GraphSceneNode, theme: GraphVisualTheme): Record<string, unknown> {
    const dimensions = nodeDimensions(node, theme);
    const labelMaxWidth = node.kind === 'group' && node.collapsed
        ? expandedGroupLabelMaxWidth(dimensions.width)
        : 160 * theme.fontSize / GRAPH_GEOMETRY.baseFontSize;
    const fitLabel = (label: string): string => node.kind !== 'group'
        ? fitGraphLabel(label, labelMaxWidth, theme)
        : node.collapsed ? fitCollapsedGroupLabel(label, labelMaxWidth, theme) : label;
    const detailLabel = fitLabel(node.label);
    const labelFontSize = node.kind === 'group' && !node.collapsed
        ? theme.fontSize * 1.2
        : graphLabelFontSize(detailLabel, labelMaxWidth, dimensions.height - 12 * theme.fontSize / GRAPH_GEOMETRY.baseFontSize, theme);
    return {
        kind: node.kind,
        passKind: node.kind === 'pass' ? node.passKind : undefined,
        resourceKind: (node.kind === 'resource' || node.kind === 'root') ? node.resourceKind : undefined,
        collapsed: node.kind === 'group' && node.collapsed ? 1 : 0,
        hasCulled: node.kind === 'group' && node.culledNodeCount > 0 ? 1 : 0,
        depthBand: node.kind === 'group' ? node.depthBand : undefined,
        detailLabel,
        labelFontSize,
        overviewLabel: fitLabel(node.overviewLabel),
        displayLabel: detailLabel,
        tooltip: node.title,
        labelMaxWidth,
        width: dimensions.width,
        height: dimensions.height,
    };
}

function edgeDefinition(edge: GraphSceneEdge): cytoscape.ElementDefinition {
    return {
        group: 'edges',
        data: {
            id: edge.id,
            source: edge.from,
            target: edge.to,
            ...edgeRenderableData(edge),
        },
    };
}

function edgeRenderableData(edge: GraphSceneEdge): Record<string, unknown> {
    const detailLabel = graphEdgeDisplayLabel(edge);
    return {
        kind: edge.kind,
        detailLabel,
        displayLabel: detailLabel,
        tooltip: edge.title,
    };
}

function viewport(core: cytoscape.Core): Viewport {
    return { pan: { ...core.pan() }, zoom: core.zoom() };
}

function restoreViewport(core: cytoscape.Core, state: Viewport): void {
    core.zoom(state.zoom);
    core.pan(state.pan);
}

function renderedPosition(core: cytoscape.Core, id: GraphSceneElementId): GraphPosition | undefined {
    const element = core.getElementById(id);
    return element.nonempty() && element.isNode() ? { ...element.renderedPosition() } : undefined;
}

export {
    createCytoscapeEdgeRouteData,
    createElkLayoutGraph,
    layoutGraphScene,
} from './panelGraphLayout.ts';
export type {
    CytoscapeEdgeRouteData,
    GraphEdgeRoute,
    GraphLayoutResult,
    GraphPosition,
} from './panelGraphLayout.ts';
export {
    createGraphStyles,
    expandedGroupLabelMaxWidth,
    graphEdgeDisplayLabel,
    graphLayoutGeometryKey,
    isOverviewGraphScale,
} from './panelGraphVisuals.ts';
